import React from 'react';
import { ChevronDown, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import i18n from '@/utils/i18n';

import {
  categoryOptions,
  isDeleteGuarded,
  memberGlyph,
  type ContainerTypeGroup,
  type GroupCategory,
  type GroupDraft,
} from './groupHelpers';

const BRAND_GREEN = 'var(--brand-green, oklch(0.685 0.163 145))';

type GroupEditorPanelProps = {
  open: boolean;
  variant: 'desktop' | 'mobile';
  mode: 'create' | 'edit';
  /** The persisted group in edit mode (drives members / linked / id). */
  group: ContainerTypeGroup | null;
  draft: GroupDraft;
  dirty: boolean;
  canSave: boolean;
  saving: boolean;
  confirmDelete: boolean;
  deleting: boolean;
  onNameChange: (value: string) => void;
  onCategoryChange: (category: GroupCategory) => void;
  onClose: () => void;
  onSubmit: () => void;
  onDeleteClick: () => void;
};

/**
 * Create/edit editor for a container type group. Shared by the desktop
 * right-hand sheet (420px) and the mobile full-screen sheet — variant only
 * changes presentation (category select vs. segmented buttons, hit targets,
 * chrome). Member assignment is read-only here; it is managed per container on
 * the Container types screen.
 */
export default function GroupEditorPanel({
  open,
  variant,
  mode,
  group,
  draft,
  dirty,
  canSave,
  saving,
  confirmDelete,
  deleting,
  onNameChange,
  onCategoryChange,
  onClose,
  onSubmit,
  onDeleteClick,
}: GroupEditorPanelProps) {
  const isMobile = variant === 'mobile';
  const isCreate = mode === 'create';
  const isEdit = !isCreate;

  const members = group?.container_types ?? [];
  const linked = group?.has_linked_stores ?? false;
  const guarded = group != null ? isDeleteGuarded(group) : true;
  const canDelete = isEdit && group != null && !guarded;

  const deleteReason = canDelete
    ? i18n.t('EditContainerTypeGroups:deleteReasonEmpty')
    : linked
      ? i18n.t('EditContainerTypeGroups:deleteReasonStores')
      : i18n.t('EditContainerTypeGroups:deleteReasonTypes');

  const catValue: string = draft.category ?? 'none';
  const handleCategory = (value: string) =>
    onCategoryChange(value === 'none' ? null : (value as GroupCategory));

  const title = isCreate
    ? i18n.t('EditContainerTypeGroups:newGroup')
    : (group?.name ?? '');
  const subtitle = isCreate
    ? i18n.t('EditContainerTypeGroups:newGroupSub')
    : i18n.t('EditContainerTypeGroups:groupNumber', { id: group?.id ?? '' });

  const header = (
    <div className="tw:flex tw:flex-none tw:items-start tw:justify-between tw:gap-3 tw:border-b tw:border-border tw:px-5 tw:pb-3.5 tw:pt-4.5">
      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-start tw:gap-1">
        {isMobile && (
          <Button
            variant="ghost"
            size="icon-sm"
            className="tw:-ml-2 tw:mt-0.5"
            aria-label={i18n.t('Common:close')}
            onClick={onClose}
          >
            <ChevronDown size={20} />
          </Button>
        )}
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
          {isMobile && (
            <span className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
              {subtitle}
            </span>
          )}
          <SheetTitle className="tw:truncate tw:text-lg tw:font-semibold">
            {title}
          </SheetTitle>
          {!isMobile && (
            <SheetDescription className="tw:text-[12.5px] tw:text-muted-foreground">
              {subtitle}
            </SheetDescription>
          )}
          {isMobile && (
            <SheetDescription className="tw:sr-only">
              {subtitle}
            </SheetDescription>
          )}
        </div>
      </div>
      {!isMobile && (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={i18n.t('Common:close')}
          onClick={onClose}
        >
          <X size={18} />
        </Button>
      )}
    </div>
  );

  const body = (
    <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto">
      <div className="tw:flex tw:flex-col tw:gap-5 tw:px-5 tw:py-5">
        {/* Group name */}
        <div className="tw:flex tw:flex-col tw:gap-1.5">
          <Label className="tw:text-[13px] tw:font-semibold">
            {i18n.t('EditContainerTypeGroups:groupName')}
          </Label>
          <Input
            value={draft.name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder={i18n.t('EditContainerTypeGroups:groupNamePlaceholder')}
            className={isMobile ? 'tw:h-11' : undefined}
            autoFocus={isCreate}
          />
        </div>

        {/* Category */}
        <div className="tw:flex tw:flex-col tw:gap-1.5">
          <Label className="tw:text-[13px] tw:font-semibold">
            {i18n.t('EditContainerTypeGroups:category')}
          </Label>
          {isMobile ? (
            <div className="tw:flex tw:gap-1.5">
              {categoryOptions().map((opt) => {
                const active = catValue === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleCategory(opt.value)}
                    className={cn(
                      'tw:inline-flex tw:h-11 tw:flex-1 tw:items-center tw:justify-center tw:rounded-md tw:border tw:text-sm tw:font-medium tw:transition-colors tw:cursor-pointer',
                      active
                        ? 'tw:border-primary tw:bg-primary tw:text-primary-foreground'
                        : 'tw:border-border tw:bg-background tw:text-foreground tw:shadow-xs tw:hover:bg-accent',
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          ) : (
            <Select value={catValue} onValueChange={handleCategory}>
              <SelectTrigger className="tw:w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions().map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <span className="tw:text-[12.5px] tw:leading-relaxed tw:text-muted-foreground">
            {i18n.t('EditContainerTypeGroups:categoryHelper')}
          </span>
        </div>

        {isEdit && (
          <>
            {/* Container types (read-only) */}
            <div className="tw:flex tw:flex-col tw:gap-2">
              <div className="tw:flex tw:items-center tw:justify-between">
                <Label className="tw:text-[13px] tw:font-semibold">
                  {i18n.t('EditContainerTypeGroups:containerTypes')}
                </Label>
                <span className="tw:text-xs tw:text-muted-foreground">
                  {i18n.t('EditContainerTypeGroups:typesAssigned', {
                    count: members.length,
                  })}
                </span>
              </div>
              <div className="tw:overflow-hidden tw:rounded-md tw:border tw:border-border">
                {members.length === 0 ? (
                  <div className="tw:px-3 tw:py-4 tw:text-center tw:text-[13px] tw:text-muted-foreground">
                    {i18n.t('EditContainerTypeGroups:noTypesAssigned')}
                  </div>
                ) : (
                  members.map((member) => {
                    const Glyph = memberGlyph(
                      (group?.category as GroupCategory) ?? null,
                    );
                    return (
                      <div
                        key={member.id}
                        className="tw:-mt-px tw:flex tw:items-center tw:gap-2.5 tw:border-t tw:border-border tw:bg-background tw:px-3 tw:py-2"
                      >
                        <span className="tw:flex tw:size-[30px] tw:flex-none tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-muted tw:text-muted-foreground">
                          {member.image_url ? (
                            <img
                              src={member.image_url}
                              alt={member.name}
                              className="tw:size-full tw:object-cover"
                            />
                          ) : (
                            <Glyph size={15} />
                          )}
                        </span>
                        <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-[13.5px]">
                          {member.name}
                        </span>
                        <span className="tw:font-mono tw:text-[11.5px] tw:text-muted-foreground">
                          #{member.id}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
              <span className="tw:text-[12.5px] tw:leading-relaxed tw:text-muted-foreground">
                {i18n.t('EditContainerTypeGroups:assignmentNote')}
              </span>
            </div>

            {/* Linked stores */}
            <div className="tw:flex tw:items-center tw:justify-between tw:rounded-md tw:border tw:border-border tw:bg-muted tw:px-3 tw:py-2.5">
              <span className="tw:text-[13px] tw:font-medium">
                {i18n.t('EditContainerTypeGroups:linkedStores')}
              </span>
              <div className="tw:flex tw:items-center tw:gap-2">
                <span
                  className="tw:size-[7px] tw:flex-none tw:rounded-full"
                  style={{
                    backgroundColor: linked ? BRAND_GREEN : 'var(--border)',
                  }}
                />
                <span className="tw:text-[13px] tw:text-muted-foreground">
                  {linked
                    ? i18n.t('EditContainerTypeGroups:linkedYes')
                    : i18n.t('EditContainerTypeGroups:linkedNo')}
                </span>
              </div>
            </div>

            {/* Danger zone */}
            <div className="tw:flex tw:flex-col tw:gap-2 tw:border-t tw:border-border tw:pt-4">
              <Label className="tw:text-[13px] tw:font-semibold tw:text-destructive">
                {i18n.t('EditContainerTypeGroups:dangerZone')}
              </Label>
              <span className="tw:text-[12.5px] tw:leading-relaxed tw:text-muted-foreground">
                {deleteReason}
              </span>
              <Button
                variant="outline"
                onClick={onDeleteClick}
                disabled={!canDelete || deleting}
                className={cn(
                  'tw:self-start',
                  isMobile && 'tw:h-11',
                  canDelete &&
                    'tw:border-destructive tw:text-destructive tw:hover:bg-destructive/10 tw:hover:text-destructive',
                )}
                style={
                  canDelete && confirmDelete
                    ? {
                        backgroundColor:
                          'color-mix(in oklab, var(--destructive) 10%, transparent)',
                      }
                    : undefined
                }
              >
                <Trash2 size={15} />
                {confirmDelete
                  ? i18n.t(
                      isMobile
                        ? 'EditContainerTypeGroups:confirmDeleteMobile'
                        : 'EditContainerTypeGroups:confirmDeleteDesktop',
                    )
                  : i18n.t('EditContainerTypeGroups:deleteGroup')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const footer = (
    <div
      className={cn(
        'tw:flex tw:flex-none tw:items-center tw:gap-2.5 tw:border-t tw:border-border tw:bg-background tw:px-5 tw:py-3.5',
        isMobile && 'tw:pb-[max(16px,env(safe-area-inset-bottom))]',
      )}
    >
      {dirty && (
        <span className="tw:inline-flex tw:min-w-0 tw:items-center tw:gap-1.5 tw:text-xs tw:text-muted-foreground">
          <span
            className="tw:size-[7px] tw:flex-none tw:rounded-full"
            style={{ backgroundColor: BRAND_GREEN }}
          />
          <span className="tw:truncate">
            {i18n.t(
              isMobile
                ? 'EditContainerTypeGroups:unsavedShort'
                : 'EditContainerTypeGroups:unsavedChanges',
            )}
          </span>
        </span>
      )}
      <div className="tw:flex tw:flex-1 tw:justify-end tw:gap-2">
        <Button
          variant="outline"
          onClick={onClose}
          className={isMobile ? 'tw:h-11' : undefined}
        >
          {i18n.t('Common:cancel')}
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!canSave || saving}
          className={isMobile ? 'tw:h-11' : undefined}
        >
          {saving
            ? i18n.t('Common:saving')
            : isCreate
              ? i18n.t('EditContainerTypeGroups:createGroupButton')
              : i18n.t('Common:saveChanges')}
        </Button>
      </div>
    </div>
  );

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        showCloseButton={false}
        className={
          isMobile
            ? 'tw:gap-0 tw:rounded-t-2xl tw:border-t-0 tw:p-0 tw:data-[side=bottom]:h-[calc(100dvh-24px)] tw:data-[side=bottom]:max-h-[calc(100dvh-24px)]'
            : 'tw:w-[420px] tw:max-w-[94%] tw:gap-0 tw:p-0 tw:data-[side=right]:sm:max-w-[420px]'
        }
      >
        <div className="tw:flex tw:h-full tw:min-h-0 tw:flex-col">
          {header}
          {body}
          {footer}
        </div>
      </SheetContent>
    </Sheet>
  );
}
